import { useCarouselCardPosition } from '@iheartjane/menu';
import { Hide, useMobileMediaQuery } from '@iheartjane/reefer';
import { useEffect, useRef } from 'react';

import { isSortedByPrice } from '@jane/search/util';

import { BloomMenuProductCard } from '../bloomMenuProductCard/bloomMenuProductCard';
import { DisplayMode, useProductCardContext } from '../productCardProvider';
import { MenuProductConfirmationView } from './confirmationView';
import { MenuProductCardDefaultView } from './defaultView';
import { MenuProductCardEditView } from './editView';
import type { RenderProps } from './listViewStateContainer';
import { ListViewStateContainer } from './listViewStateContainer';
import {
  MENU_PRODUCT_ITEM_HEIGHT,
  MENU_PRODUCT_MOBILE_ITEM_HEIGHT,
} from './utils/productCardHelper';

interface MenuProductCardContainerProps {
  shouldDisplaySponsoredTag: boolean;
}

export const MenuProductCardContainer = ({
  shouldDisplaySponsoredTag,
}: MenuProductCardContainerProps) => {
  const {
    appMode,
    cartProduct,
    displayMode,
    hideActions,
    menuProduct,
    searchState,
    maxWeightOption,
    showOnlyWeights,
  } = useProductCardContext();
  const isMobile = useMobileMediaQuery({});
  const menuMobileHeight = MENU_PRODUCT_MOBILE_ITEM_HEIGHT;
  const menuDesktopHeight = MENU_PRODUCT_ITEM_HEIGHT;

  const baseHeight = isMobile ? menuMobileHeight : menuDesktopHeight;
  const menuProductCardHeight = hideActions ? baseHeight - 48 : baseHeight;

  const cardRef = useRef<HTMLDivElement>(null);
  const prevDisplayModeRef = useRef<DisplayMode>(displayMode);

  useEffect(() => {
    const prevMode = prevDisplayModeRef.current;
    const currentMode = displayMode;

    // Focus card wrapper when returning from Edit to Product (back button) or entering Confirmation view (product added/updated)
    if (
      (prevMode === DisplayMode.Edit && currentMode === DisplayMode.Product) ||
      currentMode === DisplayMode.Confirmation
    ) {
      cardRef.current?.focus();
    }

    prevDisplayModeRef.current = currentMode;
  }, [displayMode]);

  const carouselPosition = useCarouselCardPosition();

  const getCardAriaLabel = () => {
    const positionSuffix = carouselPosition ? `, ${carouselPosition}` : '';
    switch (displayMode) {
      case DisplayMode.Confirmation:
        return 'Bag updated successfully';
      case DisplayMode.Edit:
        return `${menuProduct?.name} options`;
      case DisplayMode.Product:
      default:
        return `${menuProduct?.name} product card${positionSuffix}`;
    }
  };

  const productCardStatusMessage =
    displayMode === DisplayMode.Edit
      ? `${cartProduct && !!cartProduct.length ? 'Update' : 'Select'} available options for ${menuProduct?.name}`
      : '';

  return (
    <ListViewStateContainer sortedByPrice={isSortedByPrice(searchState)}>
      {(props: RenderProps) => {
        const { shoppingDisabled, selectedQuantity } = props;
        return (
          <>
            {appMode !== 'bloom' ? (
              <>
                <Hide isHidden aria-live="polite">
                  {productCardStatusMessage}
                </Hide>
                <div
                  ref={cardRef}
                  tabIndex={-1}
                  aria-label={getCardAriaLabel()}
                >
                  {displayMode === DisplayMode.Confirmation && (
                    <MenuProductConfirmationView
                      {...props}
                      key={`${menuProduct?.id}-confirm`}
                      height={menuProductCardHeight}
                    />
                  )}
                  {displayMode === DisplayMode.Edit && (
                    <MenuProductCardEditView
                      {...props}
                      key={`${menuProduct?.id}-edit`}
                      showOnlyWeights={showOnlyWeights}
                      maxWeightOption={maxWeightOption}
                      height={menuProductCardHeight}
                      selectedQuantity={selectedQuantity}
                      shoppingDisabled={shoppingDisabled}
                    />
                  )}
                  {displayMode === DisplayMode.Product && (
                    <MenuProductCardDefaultView
                      key={`${menuProduct?.id}-default`}
                      height={menuProductCardHeight}
                      maxWeightOption={maxWeightOption}
                      onAddToCartPressed={props.onAddToCartPressed}
                      shoppingDisabled={shoppingDisabled}
                    />
                  )}
                </div>
              </>
            ) : (
              <BloomMenuProductCard
                onAddToCartPressed={props.onAddToCartPressed}
                shouldDisplaySponsoredTag={shouldDisplaySponsoredTag}
                weights={props.weights}
              />
            )}
          </>
        );
      }}
    </ListViewStateContainer>
  );
};
